import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ChevronRight, Check, FileDown, Package, Truck, Award, Palette } from "lucide-react";

export interface ColorVariant {
  name: string;
  image: string;
}

export interface ProductVariant {
  ref?: string;
  name?: string;
  capacity?: string;
  dimensions?: string;
  diameter?: string;
  height?: string;
  size?: string;
  material: string;
  packaging: string;
  palette: string;
  image: string;
  color?: string;
  availableColors?: ColorVariant[];
  isNew?: boolean;
}

export interface LifestyleImage {
  src: string;
  caption: string;
}

export interface ProductPageConfig {
  name: string;
  slug?: string;
  breadcrumbName: string;
  subtitle: string;
  features: string[];
  variants: ProductVariant[];
  lifestyleImages?: LifestyleImage[];
  customization?: {
    maxColors: number;
    minOrder: string;
    bestPrice: string;
  };
}

const ProductPageLayout = ({ config }: { config: ProductPageConfig }) => {
  const location = useLocation();
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [availability, setAvailability] = useState<string>("en_stock");

  useEffect(() => {
    const fetchAvailability = async () => {
      // Derive slug from URL path: /produits/bols-kraft -> bols-kraft
      const slug = config.slug || location.pathname.split("/").pop() || "";
      const { data } = await supabase
        .from("products")
        .select("availability")
        .eq("slug", slug)
        .maybeSingle();
      if (data) setAvailability(data.availability);
    };
    fetchAvailability();
  }, [config.slug, location.pathname]);

  const variant = config.variants[selectedVariantIndex];
  const currentImage = variant.availableColors
    ? variant.availableColors[selectedColorIndex]?.image || variant.image
    : variant.image;

  const handleVariantChange = (i: number) => {
    setSelectedVariantIndex(i);
    setSelectedColorIndex(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Breadcrumb */}
        <section className="pt-24 pb-4">
          <div className="container mx-auto px-4">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/produits" className="hover:text-primary transition-colors">Produits</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground font-medium">{config.breadcrumbName}</span>
            </nav>
          </div>
        </section>

        {/* Hero: Image + Info */}
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
              {/* Left: Product Image */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-background border border-border rounded-2xl p-8 md:p-12 flex items-center justify-center aspect-square">
                  <img
                    src={currentImage}
                    alt={config.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>

                {/* Variant Thumbnails */}
                {config.variants.length > 1 && (
                  <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                    {config.variants.map((v, i) => (
                      <button
                        key={i}
                        onClick={() => handleVariantChange(i)}
                        className={`shrink-0 w-20 h-20 rounded-lg border-2 p-2 transition-all ${
                          i === selectedVariantIndex
                            ? "border-primary bg-secondary/30"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <img
                          src={v.image}
                          alt={v.capacity || v.name || ""}
                          className="w-full h-full object-contain"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Color Variants */}
                {variant.availableColors && variant.availableColors.length > 1 && (
                  <div className="flex gap-3 mt-3">
                    {variant.availableColors.map((c, i) => (
                      <button
                        key={c.name}
                        onClick={() => setSelectedColorIndex(i)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          i === selectedColorIndex
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary text-foreground hover:bg-secondary/80"
                        }`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Right: Product Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <h1 className="font-display text-3xl md:text-4xl font-bold text-accent">
                    {config.name}
                  </h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                    availability === "en_stock"
                      ? "bg-eco/15 text-eco"
                      : availability === "stock_faible"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-destructive/10 text-destructive"
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      availability === "en_stock"
                        ? "bg-eco"
                        : availability === "stock_faible"
                        ? "bg-amber-500"
                        : "bg-destructive"
                    }`} />
                    {availability === "en_stock" ? "En stock" : availability === "stock_faible" ? "Stock faible" : "Rupture"}
                  </span>
                </div>
                <p className="text-muted-foreground text-lg mb-6">{config.subtitle}</p>

                {/* Features Checklist */}
                <div className="space-y-3 mb-8">
                  {config.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-eco/20 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-eco" />
                      </div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="space-y-3">
                  <Button variant="kraft" size="lg" asChild className="w-full md:w-auto">
                    <Link to="/devis">Demander un devis</Link>
                  </Button>
                  <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                    <FileDown className="w-4 h-4" />
                    Télécharger la fiche technique
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Dimensions & Spécifications */}
        <section className="py-12 border-t border-border">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl font-bold text-accent mb-8">
              Dimensions & Spécifications
            </h2>

            {/* Variant Selector Tabs */}
            {config.variants.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {config.variants.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => handleVariantChange(i)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      i === selectedVariantIndex
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground hover:bg-secondary/80"
                    }`}
                  >
                    {v.capacity || v.name || v.size || `Modèle ${i + 1}`}
                  </button>
                ))}
              </div>
            )}

            {/* Specs Table */}
            <div className="bg-card rounded-xl border border-border overflow-hidden max-w-2xl">
              <table className="w-full">
                <tbody className="divide-y divide-border">
                  {variant.ref && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium w-1/3">Référence</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.ref}</td>
                    </tr>
                  )}
                  {variant.capacity && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Capacité</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.capacity}</td>
                    </tr>
                  )}
                  {variant.dimensions && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Dimensions</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.dimensions}</td>
                    </tr>
                  )}
                  {variant.diameter && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Diamètre</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.diameter}</td>
                    </tr>
                  )}
                  {variant.height && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Hauteur</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.height}</td>
                    </tr>
                  )}
                  {variant.size && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Taille</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.size}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Matière</td>
                    <td className="px-6 py-4 text-foreground font-medium">{variant.material}</td>
                  </tr>
                  {variant.color && (
                    <tr>
                      <td className="px-6 py-4 text-muted-foreground font-medium">Couleur</td>
                      <td className="px-6 py-4 text-foreground font-medium">{variant.color}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Conditionnement</td>
                    <td className="px-6 py-4 text-foreground font-medium">{variant.packaging}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-muted-foreground font-medium">Palette</td>
                    <td className="px-6 py-4 text-foreground font-medium">{variant.palette}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Mise en situation */}
        {config.lifestyleImages && config.lifestyleImages.length > 0 ? (
          <section className="py-12 bg-secondary/20 border-t border-border">
            <div className="container mx-auto px-4">
              <h2 className="font-display text-2xl font-bold text-accent mb-8">Mise en situation</h2>
              <div className={`grid gap-6 ${config.lifestyleImages.length === 1 ? "max-w-2xl mx-auto" : config.lifestyleImages.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                {config.lifestyleImages.map((img, i) => (
                  <div key={i} className="text-center">
                    <div className="rounded-xl overflow-hidden aspect-[4/3] mb-3">
                      <img src={img.src} alt={img.caption} className="w-full h-full object-cover" />
                    </div>
                    <p className="text-sm text-muted-foreground">{img.caption}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* Personnalisation */}
        {config.customization && (
          <section className="py-12 border-t border-border">
            <div className="container mx-auto px-4">
              <h2 className="font-display text-2xl font-bold text-accent mb-8">
                Personnalisation disponible
              </h2>
              <div className="bg-card rounded-xl border border-border p-6 md:p-8 max-w-3xl">
                <div className="flex flex-wrap gap-4 mb-6">
                  {Array.from({ length: config.customization.maxColors }, (_, i) => {
                    const colors = ["bg-blue-500", "bg-red-500", "bg-red-600", "bg-amber-500"];
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-sm ${colors[i]}`} />
                        <span className="text-sm font-medium text-foreground">
                          {i + 1} couleur{i > 0 ? "s" : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2 mb-6 text-sm">
                  <p className="text-muted-foreground">
                    Minimum fabrication :{" "}
                    <span className="font-medium text-foreground">{config.customization.minOrder}</span>
                  </p>
                  <p className="text-muted-foreground">
                    Meilleur prix :{" "}
                    <span className="font-medium text-foreground">{config.customization.bestPrice}</span>
                  </p>
                </div>
                <Button variant="kraft" asChild>
                  <Link to="/devis">Recevoir un devis personnalisé</Link>
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Nos avantages */}
        <section className="py-12 border-t border-border bg-secondary/20">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-2xl font-bold text-accent mb-8 text-center">
              Nos avantages
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Qualité certifiée</h3>
                <p className="text-sm text-muted-foreground">
                  Produits certifiés FSC, conformes aux normes européennes
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-eco/10 flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-eco" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Stock disponible</h3>
                <p className="text-sm text-muted-foreground">
                  Livraison rapide depuis notre entrepôt en France
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-medium text-foreground mb-2">Livraison France</h3>
                <p className="text-sm text-muted-foreground">
                  Livraison sur toute la France métropolitaine
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductPageLayout;
